import { getAccessToken } from './authClient'
import {
  ForbiddenError,
  ProblemError,
  UnauthorizedError,
  isProblem,
  type Problem,
} from './problem'
import type { components } from './api.types'

/**
 * The HTTP client. The SKELETON only, per §4.1 — AIVY's `api.ts` is 902 lines at
 * `b0da0e2` and the rest of it is chat, memory, voice and coaching.
 *
 * Ported: `API_BASE`, `getHeaders`, `fetchWithTimeout`, `throwApiError`, the
 * typed sentinels and the auth-expired event. Not ported: `responseErrorMessage`,
 * which digs through `detail`/`message`/`error` keys and JSON.stringifies an
 * object detail straight into a user-facing banner. BrainSpot returns one
 * envelope everywhere, so it is parsed once, properly — see ./problem.ts.
 */

export type MeResponse = components['schemas']['MeResponse']
export type AdminUserView = components['schemas']['AdminUserView']
export type AdminUserPage = components['schemas']['AdminUserPage']
export type AdminLlmUsage = components['schemas']['AdminLlmUsage']
export type AuditPage = components['schemas']['AuditPage']
export type AuditRecord = components['schemas']['AuditRecord']
export type Job = components['schemas']['Job']
export type JobPage = components['schemas']['JobPage']
export type JobStatus = components['schemas']['JobStatus']
export type RecordingPage = components['schemas']['RecordingPage']
export type RecordingRecord = components['schemas']['RecordingRecord']
export type ComparisonResult = components['schemas']['ComparisonResult']
export type ComparisonFeatureDelta = components['schemas']['ComparisonFeatureDelta']
export type ComparisonScoreDelta = components['schemas']['ComparisonScoreDelta']
export type ComparisonJournal = components['schemas']['ComparisonJournal']
export type RecordingStatus = components['schemas']['RecordingStatus']
export type Condition = components['schemas']['Condition']
export type SelfReport = components['schemas']['SelfReport']
export type SelfReportPatch = components['schemas']['SelfReportPatch']
export type SelfReportProvenance = components['schemas']['SelfReportProvenance']
export type SelfReportOrigin = components['schemas']['SelfReportOrigin']
export type UploadGrant = components['schemas']['UploadGrant']
export type BaselineStatus = components['schemas']['BaselineStatus']
export type BaselineVector = components['schemas']['BaselineVector']
export type Role = components['schemas']['MeResponse']['role']
export type TrackingCitation = components['schemas']['TrackingCitation']
export type TrackingCitations = components['schemas']['TrackingCitations']
export type HomeResponse = components['schemas']['HomeResponse']
export type HomeDisplay = components['schemas']['HomeDisplay']
export type JournalSummaryResponse = components['schemas']['JournalSummaryResponse']
export type JournalGroupSummary = components['schemas']['JournalGroupSummary']
export type JournalUsual = components['schemas']['JournalUsual']
export type JournalCoverage = components['schemas']['JournalCoverage']
export type JournalFacts = components['schemas']['JournalFacts']
/** `"week" | "month"` — what the route groups the days by. */
export type JournalGrouping = JournalSummaryResponse['group']
/**
 * Why a group or "Your usual" has no facts. Derived from the wire rather than
 * relisted, so a code the backend adds arrives here as a WIDER union and the
 * `Record` that turns it into a sentence
 * (`components/history/journalFacts.ts`) stops compiling until the screen has
 * words for it. One code today, `"too_few_journals"`.
 */
export type JournalSummaryUnavailable = NonNullable<JournalGroupSummary['unavailable_reason']>

/**
 * The Daily Status, as the wire actually types it.
 *
 * `DailyStatusPayload` is `{"type": "object", "additionalProperties": true}` in
 * the OpenAPI document, so the generated alias above carries no fields at all.
 * Nothing here narrows it -- `src/lib/dailyStatus.ts` owns that, and owns it in
 * one place so the narrowing is reviewable rather than sprinkled through six
 * screens. This alias exists to make the call sites say what they return.
 */
export type RawDailyStatus = components['schemas']['DailyStatusPayload']

export const API_BASE = import.meta.env.VITE_API_URL || ''

/** §3.2 renames AIVY's `aivy:auth-expired`. */
export const AUTH_EXPIRED_EVENT = 'brainspot:auth-expired'

const DEFAULT_TIMEOUT_MS = 15_000

function emitAuthExpired() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
}

export async function getHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = await getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeoutMs)

  /**
   * ── THE CALLER'S SIGNAL WAS BEING DISCARDED ────────────────────────────────
   *
   * `{ ...options, signal: controller.signal }` puts the spread FIRST, so the
   * explicit key overwrote whatever the caller passed. Every `AbortController`
   * in the hooks was therefore inert: `useResource`, `usePagedList`, `useFanOut`
   * and `useJobPolling` all abort on unmount, and the request ran to completion
   * or to the 15s timeout regardless. Only the cooperative `cancelled` flags
   * were doing anything, and each of those files says in its comments that the
   * request is cancelled.
   *
   * The visible consequence: a session that expires while Record is polling
   * emits `AUTH_EXPIRED`, the hook stops, the app navigates to sign-in -- and up
   * to 15 seconds later the un-aborted poll answers 401 and emits the event a
   * SECOND time, replacing whatever the sign-in form was showing with "Your
   * sign-in expired".
   *
   * Forwarded rather than replaced, so the timeout still applies: whichever
   * fires first aborts the request.
   */
  const caller = options.signal
  if (caller) {
    if (caller.aborted) controller.abort()
    else caller.addEventListener('abort', () => controller.abort(), { once: true })
  }

  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id))
}

/** Read the Problem envelope, if this response carries one. */
async function readProblem(res: Response): Promise<Problem | null> {
  try {
    // A clone, so a caller that wants the raw body can still read it.
    const body = await res.clone().json()
    return isProblem(body) ? body : null
  } catch {
    // Empty body, HTML from a proxy, or a gateway that never reached the app.
    return null
  }
}

export async function throwApiError(res: Response): Promise<never> {
  const problem = await readProblem(res)

  if (res.status === 401) {
    // The event is what signs the user out; the context listens for it. Emitted
    // BEFORE throwing so the sign-out happens even where a caller swallows the
    // rejection.
    emitAuthExpired()
    throw new UnauthorizedError(problem)
  }

  // 403 must NOT emit the event. The credentials were fine -- a pending account
  // or a non-admin -- and signing such a user out would replace an explanatory
  // screen with a login form, which is the one thing they cannot act on.
  if (res.status === 403) throw new ForbiddenError(problem)

  if (problem) throw new ProblemError(problem)
  throw new ProblemError({
    title: 'Request failed',
    status: res.status,
    detail: `The server answered ${res.status} with no error body.`,
    code: 'http.unparsable',
  })
}

interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  timeoutMs?: number
  /** Sent as `Idempotency-Key`. Optional on every POST that accepts it. */
  idempotencyKey?: string
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs, idempotencyKey } = options
  const headers = await getHeaders()
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  const res = await fetchWithTimeout(
    `${API_BASE}${path}`,
    {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    },
    timeoutMs,
  )

  if (!res.ok) await throwApiError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Build a query string, dropping empty values so `?cursor=` never appears. */
export function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

/* ── The routes this sprint uses ─────────────────────────────────────────── */

/**
 * The caller's own account.
 *
 * Reachable by a suspended, rejected or deleted account, unlike every other
 * route — it is mounted on the backend's `status_router` precisely so a blocked
 * user can be told why. See `brainspot/api/routes/me.py:56`.
 */
export const getMe = () => request<MeResponse>('/api/v1/me')

/* ── Admin ───────────────────────────────────────────────────────────────────
 *
 * Seventeen operations over sixteen paths (DELETE and GET share
 * `/admin/users/{user_id}`). Every list is `{items, next_cursor}`; `limit`
 * defaults to 50 and is CLAMPED to 200 rather than refused.
 *
 * There is no `sort`, and that is the backend's decision rather than an
 * oversight — `api/routes/admin.py:81` explains that a keyset cursor and an
 * arbitrary sort column cannot both be honest on one index. §5.3 asks for a
 * sort control; we do not ship one.
 */

export interface Page<T> {
  items: T[]
  next_cursor?: string | null
}

export interface ListParams {
  cursor?: string | null
  limit?: number
}

export const listPendingUsers = (p: ListParams = {}) =>
  request<AdminUserPage>(`/api/v1/admin/users/pending${query({ ...p })}`)

export const listUsers = (p: ListParams & { q?: string; status?: string } = {}) =>
  request<AdminUserPage>(`/api/v1/admin/users${query({ ...p })}`)

export const getUser = (userId: string) =>
  request<AdminUserView>(`/api/v1/admin/users/${encodeURIComponent(userId)}`)

const userAction = (userId: string, action: string, body?: unknown) =>
  request<AdminUserView>(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/${action}`,
    { method: 'POST', body },
  )

export const approveUser = (userId: string) => userAction(userId, 'approve')
export const reactivateUser = (userId: string) => userAction(userId, 'reactivate')
export const suspendUser = (userId: string) => userAction(userId, 'suspend')
export const restoreUser = (userId: string) => userAction(userId, 'restore')
export const resetUserPassword = (userId: string) => userAction(userId, 'reset-password')

/**
 * Re-send the identity provider's invitation. Sprint 1.8.
 *
 * The COUNTERPART to `resetUserPassword`, not an alternative: Cognito's
 * `AdminResetUserPassword` refuses an account still in `FORCE_CHANGE_PASSWORD`,
 * so each of the two is the only one that works for its half of the population.
 * An account that has signed in is refused with 409
 * `account.invite_not_applicable`, which the UI prevents rather than surfaces.
 */
export const resendInvite = (userId: string) => userAction(userId, 'resend-invite')

/** The reason is recorded in the audit row's metadata and never shown to the user. */
export const rejectUser = (userId: string, reason?: string) =>
  userAction(userId, 'reject', { reason: reason?.trim() || null })

export const changeUserRole = (userId: string, role: Role) =>
  request<AdminUserView>(`/api/v1/admin/users/${encodeURIComponent(userId)}/role`, {
    method: 'PATCH',
    body: { role },
  })

export const softDeleteUser = (userId: string) =>
  request<AdminUserView>(`/api/v1/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' })

export const requestUserDeletion = (userId: string) => userAction(userId, 'delete-request')

export const requestUserExport = (userId: string) =>
  request<Job>(`/api/v1/admin/users/${encodeURIComponent(userId)}/export`, { method: 'POST' })

export const listUserRecordings = (userId: string, p: ListParams = {}) =>
  request<RecordingPage>(`/api/v1/admin/users/${encodeURIComponent(userId)}/recordings${query({ ...p })}`)

export const listUserJobs = (userId: string, p: ListParams = {}) =>
  request<JobPage>(`/api/v1/admin/users/${encodeURIComponent(userId)}/jobs${query({ ...p })}`)

/** What a person's assistant turns and daily notes cost (agent-1.1 S2): counts and money. */
export const getUserLlmUsage = (userId: string, days = 30) =>
  request<AdminLlmUsage>(`/api/v1/admin/users/${encodeURIComponent(userId)}/llm-usage${query({ days })}`)

/**
 * An operator reading someone else's Daily Status.
 *
 * Named for the route it calls, because the product route below is one path
 * segment away and reads identically at a call site. It used to be
 * `getRecordingDailyStatus`, which is the name the product one wants and the
 * more dangerous of the two to reach for by accident.
 */
export const getAdminRecordingDailyStatus = (recordingId: string) =>
  request<RawDailyStatus>(
    `/api/v1/admin/recordings/${encodeURIComponent(recordingId)}/daily-status`,
  )

export interface AuditFilters extends ListParams {
  actor_user_id?: string
  target_type?: string
  target_id?: string
  action?: string
}

export const listAuditEvents = (p: AuditFilters = {}) =>
  request<AuditPage>(`/api/v1/admin/audit${query({ ...p })}`)

/* ── The product surface (Sprint 1.9) ─────────────────────────────────────────
 *
 * Everything above is the console an operator uses. Everything below is the
 * product a user uses, and the two are deliberately in one file with one
 * `request()`: a second client is a second place for the Problem envelope, the
 * 401 event and the access token to be handled slightly differently.
 *
 * The by-user routes take an explicit `userId` rather than reading it from a
 * context, because the backend treats a `user_id` in a path as an ASSERTION
 * checked against the actor, never as the authority. A client that could not
 * name the user it means could not exercise that check.
 */

export interface UploadGrantRequest {
  /** Optional. The server resolves the owner from the token regardless. */
  userId?: string
  /**
   * Sent as `Idempotency-Key`. **A repeat replays the ORIGINAL grant**, and
   * `UploadRequest` carries only `user_id`, so the fingerprint is effectively
   * constant per user: the key cannot tell one file from another. A new file, or
   * a grant that has expired, therefore needs a NEW key -- reusing one hands the
   * caller back the old grant with no error anywhere to say so.
   */
  idempotencyKey?: string
}

export const createUpload = ({ userId, idempotencyKey }: UploadGrantRequest = {}) =>
  request<UploadGrant>('/api/v1/uploads', {
    method: 'POST',
    body: { user_id: userId ?? null },
    idempotencyKey,
  })

/**
 * Which headband produced the archive.
 *
 * On `POST /recordings/jobs` it is OPTIONAL and an ASSERTION, never the answer
 * (brainspot-backend Sprint 1.9.2 S3-d): omitted, the server detects the
 * montage from the frozen upload; named, it validates the claim against the
 * recording and a mismatch is `session.device_conflict`. So a caller names a
 * device only when it KNOWS it -- the capture flow, which recorded with it --
 * and an archive picked from disk names none. A default here would be a claim
 * about a file nobody looked inside: it is how a classic `session.tar.gz`
 * uploaded on /record was declared a Pro and refused.
 */
// The contract's own enum, so a device the backend adds is a compile error in
// `lib/devices.ts` (which must then name it) rather than a silent gap.
export type DeviceId = RecordingRecord['device_id']

/**
 * The pair link of a paired session's recording (Sprint 1.9.13, backend B1):
 * the eyes-open block (`pairRole: 'EO'`, index 1) and the eyes-closed block
 * (`'EC'`, index 2) share one `pairId`. All three fields or none; the backend
 * refuses a partial link and a `pairRole` that disagrees with `condition`.
 */
export interface PairLink {
  pairId: string
  pairRole: Condition
  pairIndex: 1 | 2
}

/** An experiment capture has its own before/after roles, independent of EO/EC. */
export interface ExperimentLink {
  runId: string
  role: NonNullable<components['schemas']['UploadRecordIngestRequest']['capture_role']>
}

export interface RecordingJobRequest {
  userId: string
  uploadId: string
  /** EC or EO. Explicit and required -- mixing conditions in one baseline is a
   *  correctness bug, and the public request model has no default. */
  condition: Condition
  /**
   * Present only on a block of a paired session. Absent, the body carries none
   * of the three pair keys, exactly as a plain recording has always been sent.
   */
  pair?: PairLink
  experiment?: ExperimentLink
  /**
   * The headband the CALLER KNOWS recorded this upload. Omitted, the body
   * carries no `device_id` at all and the server detects the device from the
   * archive. There is no default: see `DeviceId`.
   */
  deviceId?: DeviceId
  /**
   * A DIFFERENT stable key from the upload's. Reusing one key across the two
   * endpoints is a 409 by construction: `application/idempotency.py` conflicts on
   * a changed endpoint as well as a changed body.
   */
  idempotencyKey?: string
}

export const submitRecordingJob = ({
  userId,
  uploadId,
  condition,
  deviceId,
  pair,
  experiment,
  idempotencyKey,
}: RecordingJobRequest) =>
  request<Job>('/api/v1/recordings/jobs', {
    method: 'POST',
    body: {
      user_id: userId,
      upload_id: uploadId,
      // The device that recorded, only when the caller knows it. ABSENT rather
      // than null when it does not: the key is left out of the body, so the
      // server detects the montage (see `DeviceId`).
      ...(deviceId !== undefined ? { device_id: deviceId } : {}),
      condition,
      // The pair link, all three keys or none (see `PairLink`).
      ...(pair !== undefined ? { pair_id: pair.pairId, pair_role: pair.pairRole, pair_index: pair.pairIndex } : {}),
      ...(experiment !== undefined ? { experiment_run_id: experiment.runId, capture_role: experiment.role } : {}),
    },
    idempotencyKey,
  })

export const getJob = (jobId: string, options: { signal?: AbortSignal } = {}) =>
  request<Job>(`/api/v1/jobs/${encodeURIComponent(jobId)}`, options)

/**
 * A page of the user's own recordings, **newest first by `created_at`**.
 *
 * Not by `session_date`. The cursor is keyed on `(created_at, recording_id)`
 * (`application/recordings.py`), so for a backfilled or derived session the list
 * order and the recording date disagree -- which is why "today's result" is
 * chosen by `session_date` and may sit on a later page.
 */
export const listRecordings = (
  userId: string,
  p: ListParams & { local_date?: string } = {},
  // A signal, unlike every admin list: this one is read by `usePagedList`, which
  // scans several pages and must drop the scan on unmount or sign-out rather
  // than let it run to completion against a user who has gone.
  options: { signal?: AbortSignal } = {},
) =>
  request<RecordingPage>(
    `/api/v1/users/${encodeURIComponent(userId)}/recordings${query({ ...p })}`,
    options,
  )

/** All pages for one recorder-local date. Never report a partial page as a whole day. */
export async function listDayRecordings(
  userId: string,
  localDate: string,
  options: { signal?: AbortSignal } = {},
): Promise<RecordingRecord[]> {
  const rows = new Map<string, RecordingRecord>()
  const seen = new Set<string>()
  let cursor: string | null = null
  do {
    options.signal?.throwIfAborted()
    const page = await listRecordings(userId, { local_date: localDate, limit: 100, cursor }, options)
    for (const row of page.items) {
      // Old APIs may ignore the filter. Their rows cannot establish today's state.
      if (row.local_date === localDate) rows.set(row.recording_id, row)
    }
    cursor = page.next_cursor ?? null
    if (cursor !== null) {
      if (seen.has(cursor)) throw new Error('The recordings list repeated a page cursor.')
      seen.add(cursor)
    }
  } while (cursor !== null)
  return [...rows.values()].sort((a, b) => a.session_id.localeCompare(b.session_id))
}

export const getComparison = (a: string, b: string, options: { signal?: AbortSignal } = {}) =>
  request<ComparisonResult>(`/api/v1/recordings/${encodeURIComponent(a)}/compare/${encodeURIComponent(b)}`, options)

export const getRecording = (recordingId: string, options: { signal?: AbortSignal } = {}) =>
  request<RecordingRecord>(`/api/v1/recordings/${encodeURIComponent(recordingId)}`, options)

/**
 * The PERSISTED causal Daily Status for one recording.
 *
 * 404 for a recording that has none -- an `empty` one never had a result, and a
 * `failed` one never got that far. Callers must not ask for those; the status on
 * the `RecordingRecord` is what says whether to ask at all.
 */
export const getDailyStatus = (recordingId: string, options: { signal?: AbortSignal } = {}) =>
  request<RawDailyStatus>(
    `/api/v1/recordings/${encodeURIComponent(recordingId)}/daily-status`,
    options,
  )

/**
 * REPLACE the whole journal. Not what the web form wants.
 *
 * An omitted field is cleared, `raw` included -- so a form submission through
 * this route deletes the verbatim journal the acquisition app captured, and
 * reports success. Use `patchSelfReport` for anything a person typed into a
 * browser. This exists because the route does, and because a test pins that the
 * two have not quietly become one.
 */
export const replaceSelfReport = (recordingId: string, report: SelfReport) =>
  request<RecordingRecord>(
    `/api/v1/recordings/${encodeURIComponent(recordingId)}/self-report`,
    { method: 'POST', body: report },
  )

/**
 * MERGE a partial correction (Sprint 1.9 §3.5).
 *
 * An omitted field is preserved; an explicit `null` clears it. `raw` is not on
 * the patch model, so sending one is a 400 `request.invalid` naming the field
 * rather than a silent drop -- which means the client must never put it there.
 */
export const patchSelfReport = (recordingId: string, patch: SelfReportPatch) =>
  request<RecordingRecord>(
    `/api/v1/recordings/${encodeURIComponent(recordingId)}/self-report`,
    { method: 'PATCH', body: patch },
  )

/**
 * The per-condition baseline summary.
 *
 * Returns counts and a flag -- `n_sessions`, `n_distinct_days`,
 * `low_confidence_flag` -- and deliberately NOT a numeric confidence or a target
 * day count. The two counts can legitimately disagree: three recordings in one
 * sitting are `n_sessions == 3` with `n_distinct_days == 1`.
 */
export const getBaselineStatus = (
  userId: string,
  condition: Condition,
  { deviceId, ...options }: BaselineReadOptions = {},
) =>
  request<BaselineStatus>(
    `/api/v1/users/${encodeURIComponent(userId)}/tracking/baseline${query({ condition, device_id: deviceId })}`,
    options,
  )

/**
 * The citation registry an `evidence[slot].refs` ID resolves to (V1-INTEGRATION
 * step 5.5).
 *
 * Configuration, not data: the pinned eeg-tracking release's `REFERENCES`, the
 * same for every caller, and gated like the other tracking reads. Resolve a ref
 * by EXACT match on `id` — the ID is never parsed. `title` is the package's
 * internal "Author year — gloss" label and is not the work's title;
 * `published_title` is. Read once per document through `lib/citations.ts`,
 * never from a component.
 */
export const getCitations = (options: { signal?: AbortSignal } = {}) =>
  request<TrackingCitations>('/api/v1/tracking/citations', options)

/**
 * Home's one summary (Sprint 1.9.9, `GET /users/{user_id}/home`).
 *
 * The mirror's whole top section in one read: the latest attempt, the newest
 * reading with its stored payload, the days before it, both baselines for one
 * headband, the next-reading hint and the last seven days. `timezone` is the
 * browser's IANA zone, and it decides what `today` is on the server; `query()`
 * drops an undefined one, which the server reads as UTC. Only `lib/home.ts`
 * narrows the answer, and only the shell's provider asks for it (1.9.9 P6).
 */
export const getHome = (
  userId: string,
  { condition, timezone }: { condition: Condition; timezone?: string },
  options: { signal?: AbortSignal } = {},
) =>
  request<HomeResponse>(
    `/api/v1/users/${encodeURIComponent(userId)}/home${query({ condition, timezone })}`,
    options,
  )

/**
 * The journal aggregate History's Journal view renders (Sprint 1.9.11 J4,
 * `GET /users/{user_id}/journal-summary`).
 *
 * **Every number on that view comes from here.** `brainspot/contracts/
 * journal_summary.py` is explicit that the producer rounds and the client only
 * formats: the facts arrive already rounded to the precision they are shown at,
 * `woke_at_mean` arrives as `"HH:MM"` because rendering a time from minutes
 * would *be* arithmetic, and the group labels, the day counts and the
 * "too few journals" reason arrive as data for the same reason. Nothing in the
 * client may average, total or count a journal answer
 * (`src/test/clientScience.test.ts`).
 *
 * `group` decides only how `groups` is partitioned; `usual` is the same whole
 * history either way. `timezone` is the browser's IANA zone and decides the
 * times of day only -- days are the recording's own `session_date`. `query()`
 * drops an undefined one, which the server reads as UTC and echoes back.
 *
 * **Never a 404**: a user with nothing recorded gets an empty summary, so the
 * view never has to tell the difference between "no history" and "this API is
 * older than the route".
 */
export const getJournalSummary = (
  userId: string,
  { group, timezone }: { group: JournalGrouping; timezone?: string },
  options: { signal?: AbortSignal } = {},
) =>
  request<JournalSummaryResponse>(
    `/api/v1/users/${encodeURIComponent(userId)}/journal-summary${query({ group, timezone })}`,
    options,
  )

/**
 * Which headband's baseline to read (Sprint 1.9.2 S4).
 *
 * Each device keeps its own baseline — the two montages are different analysis
 * cohorts with different feature SETS — so a user who has recorded with both has
 * two histories per condition. The backend reads the eight-channel BrainBit Pro
 * when `device_id` is omitted, which is every baseline written before adaptive
 * channel support; `query()` drops an undefined value, so leaving `deviceId` out
 * sends exactly the request this client always sent.
 *
 * In the OPTIONS object rather than a fourth positional argument, beside
 * `signal`, so no existing call site changes shape to gain it.
 */
export interface BaselineReadOptions {
  signal?: AbortSignal
  deviceId?: DeviceId
}

/**
 * The per-feature median/MAD maps.
 *
 * They are a reference point and a spread, **not** a supplied lower and upper
 * bound. Nothing in the client may turn them into a normal/abnormal range or a
 * robust-z; that arithmetic belongs to `eeg-tracking`, which is where it is
 * checked.
 */
export const getBaselineVector = (
  userId: string,
  condition: Condition,
  scope: 'full' | 'live' = 'full',
  { deviceId, ...options }: BaselineReadOptions = {},
) =>
  request<BaselineVector>(
    `/api/v1/users/${encodeURIComponent(userId)}/tracking/baseline-vector${query({ condition, scope, device_id: deviceId })}`,
    options,
  )

/* ── Data rights (built, and shipped dark until 1.11) ────────────────────────
 *
 * Reachable from the client only behind `VITE_FEATURE_DATA_RIGHTS`, which is off
 * and which `scripts/assert-build-env.mjs` refuses to let a production build turn
 * on. An export job created today terminates `failed` with
 * `feature.not_implemented` (Sprint 1.4 §4.0), and a button that reliably
 * produces a failed job is worse than no button.
 */

/**
 * 202 + a `Job`. Poll it with `getJob`; §4.1 of Sprint 1.4 dropped
 * `GET /me/export/{job_id}` because `GET /jobs/{job_id}` already does it.
 *
 * **Use the response for `job_id` and nothing else.** The export dedupe key is
 * stable per user, so a second request replays the first — returning the job as
 * it was AT CREATION, `pending` and 0%, however far it has actually got. Same
 * shape as the recording-job trap, different endpoint.
 */
export const requestMyExport = () => request<Job>('/api/v1/me/export', { method: 'POST' })

/**
 * `AdminUserView`, and not `MeResponse`. The contract says why:
 *
 * > Returns `AdminUserView` rather than `MeResponse` because the caller needs to
 * > see `deleted_at` and `purge_after` — when this happens, and by when — and
 * > `MeResponse` deliberately hides both.
 *
 * This was typed `MeResponse` until Stage 7, which is the wrong direction: it
 * hides the exact two fields a "scheduled for deletion" confirmation is made of,
 * so the natural repair is to re-fetch `/me` after the delete — and `/me` does
 * not carry them either. Nothing had noticed because nothing called it.
 *
 * Idempotent: a second call returns the existing schedule **unchanged**. Moving
 * `deleted_at` would move the retention clock.
 */
export const deleteMyAccount = () =>
  request<AdminUserView>('/api/v1/me', { method: 'DELETE' })

/** Audited runtime settings. User allowlists use canonical internal ids only. */
export type AdminAppSetting = components['schemas']['AdminAppSetting']
export const listAppSettings = () => request<components['schemas']['AdminAppSettings']>('/api/v1/admin/settings')
export const updateAppSetting = (key: AdminAppSetting['key'], value: components['schemas']['JsonValue']) =>
  request<AdminAppSetting>(`/api/v1/admin/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: { value } })
